import Groq from 'groq-sdk';
import { SUPPORT_CONTACT } from '@components/ChatBot/supportContact';

// eslint-disable-next-line no-undef
const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY || '';

// Models are tried in order for every request. Groq retires models over time —
// check https://console.groq.com/docs/models when responses start failing.
// (llama-3.x, mixtral and gemma2 were removed; that is what broke the bot before.)
const MODELS = [
  { id: 'openai/gpt-oss-120b', extra: { reasoning_effort: 'low' } },
  { id: 'openai/gpt-oss-20b', extra: { reasoning_effort: 'low' } },
  { id: 'qwen/qwen3.8-27b', extra: { reasoning_format: 'hidden' } }
];

const CONTACT_LINE = `☎️ Hotline kỹ thuật GBS: **${SUPPORT_CONTACT.hotline}** · ✉️ ${SUPPORT_CONTACT.email}`;

// Base persona + basic technical knowledge + when to escalate to a human.
const BASE_PROMPT = `Bạn là "Trợ lý kỹ thuật GSWM" — trợ lý của hệ thống thu thập & quản lý dữ liệu đồng hồ nước thông minh NB-IoT do Công ty CP GBS triển khai cho Công ty CP Cấp nước Hải Phòng (CNHP). Người dùng là nhân viên vận hành, điều độ, kỹ thuật của các chi nhánh cấp nước.

## Kiến thức kỹ thuật cơ bản bạn được phép giải thích
- **Đồng hồ NB-IoT**: đồng hồ nước có module truyền tin NB-IoT qua mạng di động (SIM IoT), định kỳ gửi chỉ số, trạng thái pin, cường độ sóng về hệ thống.
- **Online / Offline**: offline là đồng hồ không gửi dữ liệu trong khoảng thời gian quy định. Nguyên nhân thường gặp: hết/yếu pin, SIM hết hạn hoặc bị khoá, vùng phủ sóng yếu (hầm, hộp kim loại, ngập nước), anten/module hỏng, đồng hồ bị tháo. Hướng xử lý cơ bản: kiểm tra pin, SIM, vị trí lắp đặt, xem lịch sử sóng; nếu vẫn lỗi cần kỹ thuật ra hiện trường.
- **Cường độ sóng RSRP** (dBm, càng gần 0 càng tốt) theo ngưỡng của hệ thống: **Tốt ≥ -100 dBm**, **Trung bình -110 đến -100 dBm**, **Yếu < -110 dBm**. Sóng yếu làm mất gói tin, tốn pin; có thể cải thiện bằng cách nâng vị trí đồng hồ/anten, tránh vỏ kim loại.
- **Các loại cảnh báo**: *tấn công tháo rời* (đồng hồ bị tháo/mở), *tấn công từ tính* (dùng nam châm can thiệp), *rò rỉ nước* (lưu lượng nhỏ liên tục, thường do rò sau đồng hồ), *tiêu thụ bất thường* (tăng/giảm đột biến so với thói quen), *mất kết nối*, *cần đọc chỉ số thủ công* (không lấy được chỉ số tự động). Trạng thái xử lý: chưa xử lý / đã xử lý / không xử lý được.
- **Hai loại dữ liệu tiêu thụ**: *theo ngày* (tổng hợp theo chi nhánh) và *theo kỳ cước* (theo từng khách hàng, gồm chỉ số cũ, chỉ số mới, tiêu thụ).
- **Tra cứu trong hệ thống**: cảnh báo của khách hàng gõ "cảnh báo <mã KH>"; tiêu thụ chi nhánh gõ "tiêu thụ hôm nay của HP7"; tiêu thụ khách hàng gõ "tiêu thụ tháng này của <tên KH>".

## Khi nào phải chuyển cho kỹ thuật viên
Nếu câu hỏi vượt quá kiến thức cơ bản ở trên, cần thao tác trên thiết bị/máy chủ, cấu hình lệnh downlink, lỗi phần mềm, sự cố diện rộng, hoặc bạn KHÔNG chắc chắn: KHÔNG được đoán hay bịa thông số. Hãy nói ngắn gọn rằng vấn đề cần kỹ thuật viên hỗ trợ, gợi ý thông tin cần chuẩn bị (mã ĐBDD/serial/IMEI, mã khách hàng, thời điểm, hiện tượng) và ghi kèm dòng liên hệ:
${CONTACT_LINE}

## Quy tắc trả lời
- Tiếng Việt, ngắn gọn, đi thẳng vào vấn đề; dùng gạch đầu dòng khi liệt kê bước xử lý.
- Dùng **bold** cho số liệu, mã thiết bị/khách hàng, tên chi nhánh; *italic* cho nhãn. Emoji vừa phải (📊 💧 📡 🔋 ⚠️).
- Chỉ dùng số liệu có trong dữ liệu được cung cấp; không tự tạo số liệu.
- Không tự đặt ra con số kỹ thuật, ngưỡng, thời gian chờ hay khoảng cách cụ thể ngoài phần kiến thức ở trên.
- Không hứa thay GBS (ví dụ "kỹ thuật viên sẽ gọi lại"); chỉ hướng dẫn người dùng liên hệ.
- Từ chối lịch sự các câu hỏi không liên quan đến hệ thống cấp nước/đồng hồ.`;

const DATA_PROMPT = (context) => `## Dữ liệu tra cứu từ hệ thống
Dựa vào dữ liệu dưới đây để trả lời: phân tích số liệu, so sánh và đưa ra nhận xét ngắn có giá trị. Luôn nói rõ đây là dữ liệu *theo ngày* hay *theo kỳ cước* nếu liên quan.

${context}`;

// Some reasoning models wrap their thoughts in <think>…</think>
const stripThinking = (text) => text.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();

class GroqService {
  constructor() {
    this.client = null;
    this.keyRejected = false;

    if (GROQ_API_KEY) {
      try {
        this.client = new Groq({ apiKey: GROQ_API_KEY, dangerouslyAllowBrowser: true });
      } catch (error) {
        console.warn('⚠️ Khởi tạo Groq thất bại:', error?.message);
      }
    } else {
      console.warn('⚠️ REACT_APP_GROQ_API_KEY chưa được cấu hình — chatbot dùng câu trả lời mẫu.');
    }
  }

  get isAvailable() {
    return Boolean(this.client) && !this.keyRejected;
  }

  /**
   * Gửi tin nhắn đến Groq AI
   */
  async chat(message, conversationHistory = [], systemContext = '') {
    if (!this.isAvailable) {
      return this.getFallbackResponse(message, systemContext);
    }

    const messages = [
      {
        role: 'system',
        content: systemContext ? `${BASE_PROMPT}\n\n${DATA_PROMPT(systemContext)}` : BASE_PROMPT
      },
      ...conversationHistory.slice(-6).map((msg) => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.text
      })),
      { role: 'user', content: message }
    ];

    // Try each model for this request; a failure never disables the bot for later requests
    for (const model of MODELS) {
      try {
        const completion = await this.client.chat.completions.create({
          model: model.id,
          messages,
          temperature: 0.4,
          max_completion_tokens: 1200,
          stream: false,
          ...model.extra
        });
        const text = stripThinking(completion.choices?.[0]?.message?.content || '');
        if (text) return text;
      } catch (error) {
        const status = error?.status;
        console.warn(`⚠️ Groq model ${model.id} lỗi (${status || 'network'}):`, error?.message);
        // Invalid/revoked key: no point trying other models
        if (status === 401 || status === 403) {
          this.keyRejected = true;
          break;
        }
      }
    }

    return this.getFallbackResponse(message, systemContext);
  }

  /**
   * Câu trả lời khi AI không khả dụng (mất mạng, hết hạn mức, key sai…)
   */
  getFallbackResponse(message, systemContext) {
    if (systemContext && systemContext.trim().length > 0) {
      return `📊 **Kết quả tra cứu:**\n\n${systemContext}\n\n_Trợ lý AI tạm thời không phản hồi, đang hiển thị dữ liệu gốc._`;
    }

    return `Xin lỗi, trợ lý AI tạm thời chưa phản hồi được. 🙏

Bạn vẫn có thể tra cứu nhanh:
• *Cảnh báo:* "cảnh báo <mã khách hàng>"
• *Tiêu thụ chi nhánh:* "tiêu thụ hôm nay của HP7"
• *Tiêu thụ khách hàng:* "tiêu thụ tháng này của <tên KH>"

Cần hỗ trợ kỹ thuật ngay, vui lòng liên hệ:
${CONTACT_LINE}`;
  }
}

export const groqService = new GroqService();
